import type { TranslateFunction } from '../types/i18n'
import type { User, Order, Recipient } from '@/generated/types'

export type UserType = {
  id: number
  firstName: string
  lastName: string
  email: string
  phone: string
  systemNumber: string
  warehouseId: string
  balance: number
  hasOrders: boolean
  regionId: number
  address: string | null
  tin: string | null
  passportFullname: string | null
  statistics?: {
    totalRepackedWeight?: number
    totalCompletedWeight?: number
  }
}

export type OrderType = {
  id: number
  createdAt: number
  description?: string
  uid?: string
  bitrixId: number | null
  status: number
  trackingNumber?: string
  localCarrier: string | null
  localTrackingNumber: string | null
  postalService: number | null
  weight: number | null
  dimensions: string | null

  insured: boolean
  insuranceAmount: number | null

  paid: boolean
  parcelPrice: number | null
  price: number | null

  deliveryType: 0 | 1
  address: string | null
  tin: string | null
  passportFullname: string | null

  parcelAttachmentUrl: string | null
  labelAttachmentUrl: string | null
  recipient: { id: number } | null
}

export type WarehouseAnnotationKey = 'taxFree' | 'openingSoon'

export type WarehouseType = {
  id: string
  isNew: boolean
  enabled: boolean
  locationName: string
  annotationKey: WarehouseAnnotationKey
  address: {
    firstLine: string
    secondLine: string | null
    city: string
    state: string
    zip: string
    phone: string
    country: string
  }
}

// ===== ERROR HANDLING =====

const API_ERROR_CODES = {
  INVALID_PASSWORD: 'invalid_password',
  EMAIL_NOT_FOUND: 'email_not_found',
}

export function parseResponseErrors(
  errorResponse: Record<string, any>,
  t: TranslateFunction,
) {
  let errors

  if (errorResponse.data && errorResponse.data.error) {
    if (errorResponse.data.error === API_ERROR_CODES.INVALID_PASSWORD) {
      errors = { password: t('validation.invalidPassword') }
    }

    if (errorResponse.data.error === API_ERROR_CODES.EMAIL_NOT_FOUND) {
      errors = { email: t('validation.emailNotFound') }
    }
  }

  if (errorResponse.data && errorResponse.data.errors) {
    const mapValues = require('lodash/mapValues')
    errors = mapValues(errorResponse.data.errors, (value: string[] | string) =>
      Array.isArray(value) ? value.join(', ') : value,
    )
  }

  return errors
}

/**
 * Consolidated API mapping service
 * Converts snake_case API responses to camelCase for frontend consumption
 */

// ===== USER CONSTANTS =====

export const USER_DATA_KEYS = [
  'id',
  'firstName',
  'lastName',
  'email',
  'phone',
  'address',
  'tin',
  'passportFullname',
  'systemNumber',
  'warehouseId',
  'balance',
  'hasOrders',
  'regionId',
] as const

// ===== USER MAPPING =====

/**
 * Maps the generated User type (snake_case) to the legacy UserType (camelCase)
 */
export function mapUserToUserType(user: User): UserType {
  // Validate required fields with proper error messages
  const requiredFields = [
    'id',
    'first_name',
    'last_name',
    'email',
    'phone',
    'system_number',
    'warehouse_id',
    'region_id',
  ] as const
  const missingFields = requiredFields.filter((field) => !user[field])

  if (missingFields.length > 0) {
    throw new Error(
      `Invalid user data: missing required fields: ${missingFields.join(', ')}`,
    )
  }

  return {
    id: user.id!,
    firstName: user.first_name!,
    lastName: user.last_name!,
    email: user.email!,
    phone: user.phone!,
    systemNumber: user.system_number!,
    warehouseId: user.warehouse_id!,
    balance: user.balance ?? 0,
    hasOrders: user.has_orders ?? false,
    regionId: user.region_id!,
    address: user.address || null,
    tin: user.tin || null,
    passportFullname: user.passport_fullname || null,
    statistics: user.statistics
      ? {
          totalRepackedWeight: user.statistics.total_repacked_weight,
          totalCompletedWeight: user.statistics.total_completed_weight,
        }
      : undefined,
  }
}

/**
 * Maps legacy UserType to generated User type for API requests
 */
export function mapUserTypeToUser(userType: Partial<UserType>): Partial<User> {
  const user: Partial<User> = {}

  if (userType.id !== undefined) user.id = userType.id
  if (userType.firstName !== undefined) user.first_name = userType.firstName
  if (userType.lastName !== undefined) user.last_name = userType.lastName
  if (userType.email !== undefined) user.email = userType.email
  if (userType.phone !== undefined) user.phone = userType.phone
  if (userType.systemNumber !== undefined)
    user.system_number = userType.systemNumber
  if (userType.warehouseId !== undefined)
    user.warehouse_id = userType.warehouseId
  if (userType.balance !== undefined) user.balance = userType.balance
  if (userType.hasOrders !== undefined) user.has_orders = userType.hasOrders
  if (userType.regionId !== undefined) user.region_id = userType.regionId
  if (userType.address !== undefined) user.address = userType.address ?? undefined
  if (userType.tin !== undefined) user.tin = userType.tin ?? undefined
  if (userType.passportFullname !== undefined)
    user.passport_fullname = userType.passportFullname ?? undefined

  return user
}

// ===== ORDER MAPPING =====

/**
 * Maps the generated Order type (snake_case) to the legacy OrderType (camelCase)
 */
export function mapOrderToOrderType(order: Order): OrderType & {
  parcelAttachmentUrl?: string | null
  labelAttachmentUrl?: string | null
  recipient?: any
} {
  return {
    id: order.id ?? 0,
    createdAt: order.created_at ?? 0,
    description: order.description,
    uid: order.uid,
    bitrixId: order.bitrix_id ?? null,
    status: order.status ?? 0,
    trackingNumber: order.tracking_number,
    localCarrier: order.local_carrier ?? null,
    localTrackingNumber: order.local_tracking_number ?? null,
    postalService: null, // Not in new API structure
    weight: order.weight ?? null,
    dimensions: null, // Not in new API structure
    insured: order.insured ?? false,
    insuranceAmount: order.insurance_amount ?? null,
    paid: order.paid ?? false,
    parcelPrice: order.parcel_price ? parseFloat(order.parcel_price) : null,
    price: order.price ?? null,
    deliveryType: order.delivery_type ?? 0,
    address: order.address ?? null,
    tin: order.tin ?? null,
    passportFullname: order.passport_fullname ?? null,
    // Additional fields that might be needed for components
    parcelAttachmentUrl: order.parcel_attachment_url ?? null,
    labelAttachmentUrl: order.label_attachment_url ?? null,
    recipient: order.recipient
      ? mapRecipientShortToCamelCase(order.recipient)
      : null,
  }
}

/**
 * Maps order list/array responses
 */
export function mapOrderListToOrderTypeList(orders: Order[]): OrderType[] {
  return orders.map(mapOrderToOrderType)
}

// ===== RECIPIENT MAPPING =====

/**
 * Maps the generated Recipient type (snake_case) to camelCase for forms
 */
export function mapRecipientToCamelCase(recipient: Recipient) {
  return {
    id: recipient.id,
    phone: recipient.phone,
    tin: recipient.tin,
    passportFullname: recipient.passport_fullname,
    passportNumber: recipient.passport_number,
    passportIssuedBy: recipient.passport_issued_by,
    passportIssuedAt: recipient.passport_issued_at,
    passportExpiresAt: recipient.passport_expires_at,
    deliveryAddress: recipient.delivery_address,
    registrationAddress: recipient.registration_address,
    isDefault: recipient.is_default ?? false,
    cityId: recipient.city_id,
    deliveryEntranceNumber: recipient.delivery_entrance_number,
    deliveryIntercomNumber: recipient.delivery_intercom_number,
    deliveryFloorNumber: recipient.delivery_floor_number,
    deliveryFlatNumber: recipient.delivery_flat_number,
    city: recipient.city,
    createdAt: recipient.created_at,
    idPassportImageBackUrl: recipient.id_passport_image_back_url,
    idPassportImageFrontUrl: recipient.id_passport_image_front_url,
  }
}

/**
 * Helper function for mapping RecipientShort (used in orders)
 */
function mapRecipientShortToCamelCase(recipient: any) {
  return {
    id: recipient.id,
    phone: recipient.phone,
    passportFullname: recipient.passport_fullname,
    deliveryAddress: recipient.delivery_address,
    // Add other RecipientShort fields as needed
  }
}

/**
 * Maps recipient form data to snake_case for API requests
 */
export function mapRecipientFormToSnakeCase(formData: any) {
  return {
    phone: formData.phone,
    tin: formData.tin,
    passport_fullname: formData.passport_fullname,
    passport_number: formData.passport_number,
    passport_issued_by: formData.passport_issued_by,
    passport_issued_at: formData.passport_issued_at,
    passport_expires_at: formData.passport_expires_at,
    delivery_address: formData.delivery_address,
    registration_address: formData.registration_address,
    is_default: formData.is_default,
    city_id: formData.city_id ? parseInt(formData.city_id) : undefined,
    delivery_entrance_number: formData.delivery_entrance_number,
    delivery_intercom_number: formData.delivery_intercom_number,
    delivery_floor_number: formData.delivery_floor_number,
    delivery_flat_number: formData.delivery_flat_number,
  }
}

// ===== UTILITY FUNCTIONS =====

/**
 * Generic SWR data transformer for orders
 * Use this as the data transformer in SWR options
 */
export function transformOrderData(
  data: Order | Order[],
): OrderType | OrderType[] {
  if (Array.isArray(data)) {
    return mapOrderListToOrderTypeList(data)
  }
  return mapOrderToOrderType(data)
}

/**
 * Generic SWR data transformer for recipients
 */
export function transformRecipientData(data: Recipient | Recipient[]) {
  if (Array.isArray(data)) {
    return data.map(mapRecipientToCamelCase)
  }
  return mapRecipientToCamelCase(data)
}
